import type { Location } from '@medplum/fhirtypes';

import { Stack, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import type { FhirView } from '../../core/view-contract';
import { conceptText } from '../accessors';

/**
 * The exported Location component. Location hierarchies render as trees of
 * rows (the app owns the tree layout via partOf grouping), so the row is the
 * primary variant.
 */
export const LocationView: FhirView<Location> = (props) => {
  if (props.ctx.inList) return <LocationRowView {...props} />;
  return <LocationReadView {...props} />;
};

function locationName(data: Location): string {
  return data.name ?? 'Location';
}

/** The eluve layer name rides on physicalType (text wins over coding display). */
function layerText(data: Location): string | undefined {
  return conceptText(data.physicalType);
}

/** Tree/list row: name, layer, status when not active. */
const LocationRowView: FhirView<Location> = ({ data }) => {
  const layer = layerText(data);
  return (
    <Stack
      direction="horizontal"
      gap={2}
      align="center"
      data-view="location-row"
      className="min-w-0 py-2"
    >
      <Text inline size="sm" weight="regular" className="truncate">
        {locationName(data)}
      </Text>
      {layer && (
        <Text inline size="xs" color="tertiary">
          {layer}
        </Text>
      )}
      {data.status !== 'active' && <FhirStatusTag status={data.status} />}
    </Stack>
  );
};

/** Standalone card: name, layer, status, address, phone. */
const LocationReadView: FhirView<Location> = ({ data, ctx }) => {
  const layer = layerText(data);
  const address = data.address?.text;
  const phone = data.telecom?.find((point) => point.system === 'phone')?.value;
  return (
    <div
      data-view="location-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          fullWidth={false}
          className="min-w-0"
        >
          <Text inline size="sm" weight="semibold" className="truncate">
            {locationName(data)}
          </Text>
          {layer && (
            <Text inline size="xs" color="tertiary">
              {layer}
            </Text>
          )}
        </Stack>
        <FhirStatusTag status={data.status} />
      </Stack>
      <Stack
        direction="horizontal"
        wrap
        align="stretch"
        className="mt-1 gap-x-4"
      >
        {address && (
          <Text inline size="xs" color="tertiary">
            {address}
          </Text>
        )}
        {phone && (
          <Text inline size="xs" color="tertiary">
            {phone}
          </Text>
        )}
      </Stack>
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
